import { RoccoLink } from '@rocco/components/link';
import { useRef, type MouseEvent, type ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import {
    PRODUCT_HERO_TRANSITION_NAME,
    PROJECT_HERO_TRANSITION_NAME,
    recordTileMorphStart,
    startsPlainNavigation,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import type { HeroTransitionName } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { getLogoBadgeBorderColor } from '@rocco/ui/tile-professional/components/logo-badge-border';
import type { PinItemType } from '~/modules/moodboards/type';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { DetailTileAdminMenu } from '~/modules-rocco/admin/components/DetailTileAdminMenu';
import { DetailTileMedia, DETAIL_TILE_MEDIA_CLASS } from './DetailTileMedia';
import { detailTileActionsFor } from './detail-tile-actions';
import type { DetailTile } from './TileCarousel';

/**
 * The snapshot name a tile morphs under, by what it points at. A pin type with
 * no detail hero to morph into (articles, photos, member uploads) maps to
 * nothing and the tile navigates plainly. Professionals do too — the profile
 * page opens on its own intro reel, so a zoomed tile would land under an
 * animation that immediately replaces it.
 */
const heroTransitionNameFor = (
    pinType: PinItemType | undefined,
): HeroTransitionName | undefined => {
    if (pinType === 'ProductPin') return PRODUCT_HERO_TRANSITION_NAME;
    if (pinType === 'ProjectPin') return PROJECT_HERO_TRANSITION_NAME;
    return undefined;
};

/** Map a pin type to the admin item-type the directory admin actions expect. */
const adminItemTypeFor = (
    pinType: PinItemType | undefined,
): 'projects' | 'products' =>
    pinType === 'ProductPin' ? 'products' : 'projects';

export interface DetailTileCardProps {
    tile: DetailTile;
    /** `light` = ink on white. `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    hideSubtitle?: boolean;
    /** Aspect/background classes for the media box. */
    mediaClassName?: string;
    /** Classes for the media `img` (e.g. `object-contain` for square studio shots). */
    imgClassName?: string;
    /**
     * The tile's rendered width as a `sizes` string (see `getImagesSizes`), so
     * the image is served through the CDN loader at the size it lands at. Opt-in:
     * without it the source rendition is served untouched, which is what every
     * existing caller does.
     */
    sizes?: string;
    /** Classes for the title/subtitle lines. */
    textClassName?: string;
    /**
     * Classes for the subtitle line alone, applied after `textClassName`. The
     * product card sets its brand line a step below its title, which one shared
     * class cannot express.
     */
    subtitleClassName?: string;
    /** Override the corner logo badge's size / inset (e.g. the larger badge the
     *  professional tile carries in the network + brand-family grids). */
    logoClassName?: string;
    /**
     * A small label over the media's top-left corner — what marks this tile out
     * from the others around it (e.g. "Same range" in the supplier's grid).
     * Sits opposite the Save control, which owns the top-right.
     */
    badge?: ReactNode;
    onTileClick?: (tileId: number) => void;
}

/**
 * One detail-page tile — media (with the Save overlay + BM admin menu when the
 * tile opts in) plus title and subtitle lines. The single tile body shared by
 * {@link TileCarousel} and the mobile tile grids.
 */
export const DetailTileCard = ({
    tile,
    theme = 'light',
    hideSubtitle = false,
    mediaClassName,
    imgClassName,
    textClassName,
    subtitleClassName,
    logoClassName,
    badge,
    sizes,
    onTileClick,
}: DetailTileCardProps) => {
    const isDark = theme === 'dark';
    const subtitle = tile.subtitle ?? tile.professional?.name;
    const cardRef = useRef<HTMLDivElement>(null);
    // The morph runs wherever a tile does, behind the feature flag alone, so a
    // related row on a detail page zooms into its destination the same way a
    // directory tile does. Every hook runs unconditionally to keep the hook
    // order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const prefersReducedMotion = usePrefersReducedMotion();
    const heroName = heroTransitionNameFor(tile.pinType);
    const morphEnabled =
        viewTransitionsFlagOn &&
        !prefersReducedMotion &&
        heroName !== undefined;
    const heroStyle = useTileHeroViewTransitionStyle(
        morphEnabled ? tile.link : undefined,
        heroName ?? PRODUCT_HERO_TRANSITION_NAME,
    );

    const handleTileClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (morphEnabled && heroName && startsPlainNavigation(event)) {
            recordTileMorphStart(
                heroName,
                tile.link,
                cardRef.current?.querySelector<HTMLImageElement>(
                    `.${DETAIL_TILE_MEDIA_CLASS} img`,
                )?.currentSrc,
            );
        }
        onTileClick?.(tile.id);
    };
    // Clear-cut treatment mirrors rocco TileMedia (the prod tiles): per-image
    // `multiplied` flag → clear-cut fill token + contain + multiply; photos cover.
    const isMultiplied = Boolean(tile.images?.[0]?.multiplied);

    // Action panel config.
    const {
        enquiry: tileEnquiry,
        share: tileShare,
        tileInfo,
        visitWebsite: tileVisitWebsite,
    } = detailTileActionsFor(tile);

    return (
        <DetailTileAdminMenu
            adminData={tile.adminData}
            itemId={tile.id}
            itemLink={tile.link}
            professionalLink={tile.professionalLink ?? ''}
            itemType={adminItemTypeFor(tile.pinType)}
        >
            <div className="relative" ref={cardRef}>
                <RoccoLink
                    to={tile.link}
                    onClick={handleTileClick}
                    viewTransition={morphEnabled}
                    className="block w-full"
                >
                    <DetailTileMedia
                        style={heroStyle}
                        src={tile.images?.[0]?.src}
                        alt={tile.images?.[0]?.alt ?? tile.title}
                        sizes={sizes}
                        className={cn(
                            !isMultiplied && 'bg-neutral-200',
                            'max-md:aspect-[291/400] md:aspect-[446/550]',
                            mediaClassName,
                            // Last so a caller's dark section background can't win:
                            // multiply over a dark fill renders the cut-out black.
                            isMultiplied && 'bg-[var(--color-clearcut-fill)]',
                        )}
                        imgClassName={cn(
                            'max-w-none',
                            isMultiplied && 'object-contain mix-blend-multiply',
                            imgClassName,
                        )}
                        pinId={tile.pinType ? tile.id : undefined}
                        pinType={tile.pinType}
                        libraryItemId={tile.libraryItemId}
                        enquiry={tileEnquiry}
                        visitWebsite={tileVisitWebsite}
                        share={tileShare}
                        tileInfo={tileInfo}
                    >
                        {/* Above the hover strip, not under it: the strip
                        lays a dark gradient over the top of the tile, and at
                        z-[1] the badge was reading through it. */}
                        {badge ? (
                            <div className="absolute z-20 max-md:top-3 max-md:left-3 md:top-4 md:left-4">
                                {badge}
                            </div>
                        ) : null}
                        {/* Professional logo badge (network tiles) — matches the
                        corner badge on the /professionals directory tiles. */}
                        {tile.logo ? (
                            <div
                                className={cn(
                                    'absolute flex items-center justify-center overflow-hidden rounded-full max-md:top-3 max-md:left-3 max-md:size-11 md:top-4 md:left-4 md:size-14',
                                    logoClassName,
                                )}
                                style={{
                                    backgroundColor:
                                        tile.logoBackground || '#ffffff',
                                    ...(getLogoBadgeBorderColor(
                                        tile.logoBackground,
                                    )
                                        ? {
                                              border: `1px solid ${getLogoBadgeBorderColor(tile.logoBackground)}`,
                                          }
                                        : {}),
                                }}
                            >
                                <img
                                    src={tile.logo}
                                    alt=""
                                    loading="lazy"
                                    className="size-full object-contain p-2"
                                />
                            </div>
                        ) : null}
                    </DetailTileMedia>
                    <p
                        className={cn(
                            // Figma tile (1679:75719): title Neue Haas 65 Medium,
                            // 22px, leading 1.3; media→text gap 10px (0.625rem).
                            'm-0 font-sans leading-[1.3] font-medium max-md:pt-3 max-md:text-sm md:pt-[0.625rem] md:text-[1.375rem]',
                            isDark ? 'text-white' : 'text-[#181818]',
                            textClassName,
                        )}
                    >
                        {tile.title}
                    </p>
                    {!hideSubtitle && subtitle ? (
                        <p
                            className={cn(
                                // Subtitle is the title ink at 60% — the lightest
                                // step that keeps WCAG AA contrast (4.5:1) on both
                                // themes; the Figma's 40% (1679:75721) fails at 2.5:1.
                                'm-0 font-sans leading-[1.3] max-md:text-sm md:text-[1.375rem]',
                                isDark ? 'text-white/60' : 'text-[#181818]/60',
                                textClassName,
                                subtitleClassName,
                            )}
                        >
                            {subtitle}
                        </p>
                    ) : null}
                </RoccoLink>
                {/* Mobile actions live outside the tile link so a tap on the `•••`
                or a sheet-backdrop dismiss can never navigate the tile. */}
                {tile.pinType ? (
                    <TileMobileActionsSlot
                        pinId={tile.id}
                        pinType={tile.pinType}
                        libraryItemId={tile.libraryItemId}
                        enquiry={tileEnquiry}
                        visitWebsite={tileVisitWebsite}
                        share={tileShare}
                        tileInfo={tileInfo}
                    />
                ) : null}
            </div>
        </DetailTileAdminMenu>
    );
};
